import { supabase } from "@/integrations/supabase/client";
import { type Report, type ReportProject, type ReportType } from "@/types";
import { logActivity, ActivityActions } from "@/utils/activityLogger";
import { getSignedUrlFromPath, BUCKET_NAMES } from "@/lib/supabase-s3-storage";
import { isExpenseCostRecognized } from "@/lib/paymentMethods";

// Helper function to convert storage references to signed URLs for reports
async function convertStorageReferenceToSignedUrl(urlOrReference: string | null): Promise<string | null> {
  if (!urlOrReference) {
    return null;
  }

  // If it's already a regular HTTP URL, return as-is
  if (urlOrReference.startsWith('http://') || urlOrReference.startsWith('https://')) {
    return urlOrReference;
  }

  // If it's a storage reference (supabase-storage://bucket/path), convert to signed URL
  if (urlOrReference.startsWith('supabase-storage://')) {
    try {
      
      
      // Parse the storage reference: supabase-storage://bucket-name/file-path
      const match = urlOrReference.match(/^supabase-storage:\/\/([^\/]+)\/(.+)$/);
      if (!match) {
        
        return null;
      }

      const [, bucketName, filePath] = match;
      

      // Map bucket names to bucket types
      const bucketTypeMap: Record<string, 'project-images' | 'site-photos' | 'profile-avatars' | 'documents'> = {
        'private-project-images': 'project-images',
        'private-site-photos': 'site-photos', 
        'private-profile-avatars': 'profile-avatars',
        'private-documents': 'documents'
      };

      const bucketType = bucketTypeMap[bucketName];
      if (!bucketType) {
        
        return null;
      }

      // Generate signed URL valid for 24 hours (enough for report generation and viewing)
      const signedUrl = await getSignedUrlFromPath(filePath, bucketType, 3600 * 24);
      
      return signedUrl;
    } catch (error) {
      
      return null;
    }
  }

  // If it's some other format, return as-is
  return urlOrReference;
}

// Helper function to process images in entries for reports
async function processEntryImagesForReport(entries: any[]): Promise<any[]> {
  if (!entries || entries.length === 0) {
    return entries;
  }

  

  const processedEntries = await Promise.all(
    entries.map(async (entry) => {
      if (entry.image_url) {
        const signedUrl = await convertStorageReferenceToSignedUrl(entry.image_url);
        return {
          ...entry,
          image_url: signedUrl
        };
      }
      return entry;
    })
  );

  
  return processedEntries;
}

// Helper function to process images in construction stages for reports  
async function processStageImagesForReport(stages: any[]): Promise<any[]> {
  if (!stages || stages.length === 0) {
    return stages;
  }

  

  const processedStages = await Promise.all(
    stages.map(async (stage) => {
      if (stage.photo_url) {
        const signedUrl = await convertStorageReferenceToSignedUrl(stage.photo_url);
        return {
          ...stage,
          photo_url: signedUrl
        };
      }
      return stage;
    })
  );

  
  return processedStages;
}

export const reportsAPI = {
  // Get all reports for the current user
  async getReports() {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        project:projects(id, name)
      `)
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (error) {
      return { data: null, error };
    }

    // Transform data from DB format to frontend format
    const transformedData = data.map(report => ({
      id: report.id,
      type: report.type as ReportType,
      projectId: report.project_id,
      startDate: report.start_date,
      endDate: report.end_date,
      includePhotos: report.include_photos,
      includeSignatures: report.include_signatures
    }));

    return { data: transformedData as Report[], error: null };
  },

  // Get reports for a specific project
  async getProjectReports(projectId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .eq('project_id', projectId)
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (error) {
      return { data: null, error };
    }

    // Transform data from DB format to frontend format
    const transformedData = data.map(report => ({
      id: report.id,
      type: report.type as ReportType,
      projectId: report.project_id,
      startDate: report.start_date,
      endDate: report.end_date,
      includePhotos: report.include_photos,
      includeSignatures: report.include_signatures
    }));

    return { data: transformedData as Report[], error: null };
  },

  // Get a single report by ID
  async getReport(id: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('reports')
      .select(`
        *,
        project:projects(id, name)
      `)
      .eq('id', id)
      .eq('user_id', session.user.id)
      .single();

    if (error) {
      return { data: null, error };
    }

    // Transform report data to frontend format
    const transformedReport: Report = {
      id: data.id,
      type: data.type as ReportType,
      projectId: data.project_id,
      startDate: data.start_date,
      endDate: data.end_date,
      includePhotos: data.include_photos,
      includeSignatures: data.include_signatures
    };

    return { data: transformedReport, error: null };
  },

  // Create a new report
  async createReport(reportData: Partial<Report>) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Insert the report
    const { data, error } = await supabase
      .from('reports')
      .insert({
        user_id: session.user.id,
        project_id: reportData.projectId,
        type: reportData.type,
        start_date: reportData.startDate,
        end_date: reportData.endDate,
        include_photos: reportData.includePhotos,
        include_signatures: reportData.includeSignatures
      })
      .select()
      .single();

    if (error) {
      return { data: null, error };
    }

    // Log the report creation
    await logActivity(
      session.user.id,
      ActivityActions.CREATE_REPORT,
      {
        report_id: data.id,
        project_id: reportData.projectId,
        type: reportData.type,
        date_range: `${reportData.startDate} to ${reportData.endDate}`
      }
    );

    return { data: data.id, error: null };
  },

  // Update an existing report
  async updateReport(id: string, reportData: Partial<Report>) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Update the report
    const { data, error } = await supabase
      .from('reports')
      .update({
        project_id: reportData.projectId,
        type: reportData.type,
        start_date: reportData.startDate,
        end_date: reportData.endDate,
        include_photos: reportData.includePhotos,
        include_signatures: reportData.includeSignatures,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('user_id', session.user.id)
      .select()
      .single();

    if (error) {
      return { data: null, error };
    }

    // Log the report update
    await logActivity(
      session.user.id,
      ActivityActions.UPDATE_REPORT,
      {
        report_id: id,
        project_id: reportData.projectId,
        type: reportData.type,
        changes: Object.keys(reportData).join(', ')
      }
    );

    return { data: data, error: null };
  },

  // Delete a report
  async deleteReport(id: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Get report details before deletion for logging
    const { data: reportData, error: getError } = await supabase
      .from('reports')
      .select('*')
      .eq('id', id)
      .eq('user_id', session.user.id)
      .single();

    if (getError) {
      return { data: null, error: getError };
    }

    // Delete the report
    const { data, error } = await supabase
      .from('reports')
      .delete()
      .eq('id', id)
      .eq('user_id', session.user.id);

    if (!error) {
      // Log the report deletion
      await logActivity(
        session.user.id,
        ActivityActions.DELETE_REPORT,
        {
          report_id: id,
          project_id: reportData.project_id,
          type: reportData.type
        }
      );
    }

    return { data, error };
  },

  // Get available projects for reports
  async getAvailableProjects() {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('project_user_link')
      .select('project:project_id(id, name)')
      .eq('user_id', session.user.id);

    if (error) {
      return { data: null, error };
    }

    // Transform to the expected format
    const transformedData: ReportProject[] = data.map((item: any) => ({
      id: item.project.id,
      name: item.project.name
    }));

    return { data: transformedData, error: null };
  },

  // Generate HTML content for a report
  async generateReportHTML(reportData: {
    projectId: string;
    type: ReportType;
    startDate?: string;
    endDate?: string;
    includePhotos: boolean;
    includeAllRecords?: boolean;
  }) {
    
    
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {

      return { data: null, error: new Error('User not authenticated') };
    }

    // Tier gate: detailed reports are premium-only. Check the server view
    // rather than trusting the client — the client is not a security boundary.
    if (reportData.type === 'detailed') {
      const { data: tierRow } = await supabase
        .from('subscription_status_view')
        .select('tier')
        .eq('user_id', session.user.id)
        .single();
      if ((tierRow?.tier ?? 'free') !== 'premium') {
        return {
          data: null,
          error: new Error('PREMIUM_REQUIRED_DETAILED_REPORT'),
        };
      }
    }

    try {

      // Get project data
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('*')
        .eq('id', reportData.projectId)
        .single();

      if (projectError) {
        
        return { data: null, error: projectError };
      }

      // Get user data for company info
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('name, company, email')
        .eq('id', session.user.id)
        .single();

      if (userError) {
        
      }
      
      
      // Check if project has a start date and adjust startDate if needed
      if (project && project.start_date && reportData.startDate) {
        const projectStartDate = new Date(project.start_date);
        const requestedStartDate = new Date(reportData.startDate);
        
        if (requestedStartDate < projectStartDate) {
          
          reportData.startDate = project.start_date;
        }
      }
      
      let reportDetails;
      let htmlContent = '';

      // Common styles for A4 formatting - FIXED for proper PDF scaling
      const commonStyles = `
        /* A4 Page Setup - Fixed PDF scaling */
        @page {
          size: A4;
          margin: 15mm 10mm 15mm 10mm;
        }
        
        * {
          box-sizing: border-box;
        }
        
        html {
          font-size: 12px; /* Base font size for proper scaling */
        }
        
        body {
          font-family: 'Arial', 'Helvetica', sans-serif;
          line-height: 1.3;
          color: #333;
          background: white;
          margin: 0;
          padding: 0;
          font-size: 10px; /* Smaller base font for PDF */
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .report-container {
          width: 100%;
          max-width: 190mm; /* A4 width minus margins */
          margin: 0 auto;
          padding: 10px;
          background: white;
        }
        
        /* Header Styles - FIXED scaling */
        .report-header {
          border-bottom: 2px solid #4F46E5;
          padding-bottom: 10px;
          margin-bottom: 15px;
        }
        
        .report-header h1 {
          color: #4F46E5;
          font-size: 18px; /* Fixed size for PDF */
          font-weight: bold;
          margin: 0 0 3px 0;
          text-align: center;
        }
        
        .report-subtitle {
          text-align: center;
          color: #666;
          font-size: 11px; /* Fixed size */
          margin: 3px 0 10px 0;
          font-weight: 500;
        }
        
        .company-info {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-top: 10px;
          font-size: 8px; /* Fixed smaller size */
          line-height: 1.4;
        }
        
        .company-left {
          flex: 1;
        }
        
        .company-right {
          flex: 1;
          text-align: right;
        }
        
        .company-info strong {
          color: #4F46E5;
          font-weight: 600;
        }
        
        /* Project Info Section - FIXED */
        .project-info {
          background: #f8f9ff;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          padding: 10px;
          margin-bottom: 15px;
        }
        
        .project-info h2 {
          color: #4F46E5;
          font-size: 12px; /* Fixed size */
          margin: 0 0 8px 0;
          font-weight: 600;
        }
        
        .project-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        
        .detail-item {
          display: flex;
          flex-direction: column;
        }
        
        .detail-label {
          font-weight: 600;
          color: #374151;
          font-size: 7px; /* Fixed size */
          margin-bottom: 1px;
        }
        
        .detail-value {
          color: #6b7280;
          font-size: 8px; /* Fixed size */
        }
        
        /* Section Headers - FIXED */
        .section-header {
          background: #4F46E5;
          color: white;
          padding: 6px 10px;
          margin: 15px 0 10px 0;
          font-size: 11px; /* Fixed size */
          font-weight: 600;
          border-radius: 3px;
        }
        
        /* Table Styles - FIXED for PDF */
        .data-table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
          font-size: 8px; /* Fixed small size for tables */
        }
        
        .data-table th {
          background: #f3f4f6;
          color: #374151;
          padding: 5px 4px;
          text-align: left;
          font-weight: 600;
          border: 1px solid #d1d5db;
          font-size: 7px; /* Even smaller for headers */
        }
        
        .data-table td {
          padding: 4px 4px;
          border: 1px solid #e5e7eb;
          vertical-align: top;
          font-size: 8px;
        }
        
        .data-table tbody tr:nth-child(even) {
          background: #f9fafb;
        }
        
        .amount {
          text-align: right;
          font-weight: 600;
          color: #059669;
        }
        
        .amount.negative {
          color: #dc2626;
        }
        
        /* Photo Grid - FIXED */
        .photo-section {
          margin-top: 15px;
        }
        
        .photo-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
          gap: 10px;
          margin-top: 10px;
        }
        
        .photo-item {
          text-align: center;
          page-break-inside: avoid;
        }
        
        .photo-item img {
          width: 100%;
          max-width: 80px;
          height: auto;
          border: 1px solid #d1d5db;
          border-radius: 2px;
        }
        
        .photo-caption {
          font-size: 6px; /* Very small for captions */
          color: #6b7280;
          margin-top: 3px;
          line-height: 1.2;
        }
        
        /* Stage Items for General Report - FIXED */
        .stage-list {
          list-style: none;
          padding: 0;
          margin: 10px 0;
        }
        
        .stage-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          margin-bottom: 8px;
          padding: 8px;
          page-break-inside: avoid;
        }
        
        .stage-item.completed {
          background: #f0fdf4;
          border-color: #86efac;
        }
        
        .stage-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 5px;
        }
        
        .stage-title {
          font-weight: 600;
          color: #374151;
          font-size: 9px; /* Fixed size */
          margin: 0;
        }
        
        .completion-badge {
          font-size: 12px; /* Fixed size */
          font-weight: bold;
        }
        
        .completion-badge.done {
          color: #059669;
        }
        
        .completion-badge.pending {
          color: #d97706;
        }
        
        .stage-date {
          color: #6b7280;
          font-size: 7px; /* Fixed size */
          margin: 1px 0;
        }
        
        .stage-note {
          color: #4b5563;
          font-size: 7px; /* Fixed size */
          margin-top: 5px;
          font-style: italic;
        }
        
        .stage-photo {
          margin-top: 8px;
          text-align: center;
        }
        
        .stage-photo img {
          max-width: 120px;
          height: auto;
          border: 1px solid #d1d5db;
          border-radius: 2px;
        }
        
        /* Phase Headers - FIXED */
        .phase-header {
          background: linear-gradient(135deg, #4F46E5, #7C3AED);
          color: white;
          padding: 8px 12px;
          margin: 12px 0 8px 0;
          font-size: 11px; /* Fixed size */
          font-weight: 600;
          border-radius: 4px;
        }
        
        /* Footer - FIXED */
        .report-footer {
          margin-top: 20px;
          padding-top: 10px;
          border-top: 1px solid #e5e7eb;
          text-align: center;
          font-size: 6px; /* Very small footer */
          color: #6b7280;
        }
        
        /* Print Styles */
        @media print {
          .report-container {
            margin: 0;
            padding: 0;
          }
          
          .page-break {
            page-break-before: always;
          }
          
          .no-print {
            display: none !important;
          }
          
          /* Force proper sizing in print */
          body {
            font-size: 10px !important;
          }
        }
        
        /* Mobile view adjustments - keep readable on screen */
        @media screen and (max-width: 768px) {
          body {
            font-size: 12px; /* Larger for mobile viewing */
          }
          
          .report-container {
            padding: 15px;
          }
          
          .company-info {
            flex-direction: column;
            gap: 8px;
            font-size: 10px;
          }
          
          .company-right {
            text-align: left;
          }
          
          .project-details {
            grid-template-columns: 1fr;
          }
          
          .data-table {
            font-size: 10px;
          }
          
          .data-table th {
            font-size: 9px;
          }
          
          .data-table td {
            font-size: 10px;
          }
        }
      `;

      if (reportData.type === 'detailed') {
        
        // For detailed report, get all entries
        // Build query conditionally based on includeAllRecords
        let query = supabase
          .from('entries')
          .select(`
            *,
            person:people(id, name, category)
          `)
          .eq('project_id', reportData.projectId);

        // Only apply date filters if includeAllRecords is false
        if (!reportData.includeAllRecords) {
          query = query
            .gte(reportData.startDate ? 'date' : 'created_at', reportData.startDate || '1970-01-01')
            .lte(reportData.endDate ? 'date' : 'created_at', reportData.endDate || new Date().toISOString());
        }

        const { data: entries, error: entriesError } = await query.order('date', { ascending: false });

        if (entriesError) {
          
          return { data: null, error: entriesError };
        }

        
        
        // Process entry images - convert storage references to signed URLs
        const processedEntries = await processEntryImagesForReport(entries || []);
        
        
        
        // Filter expense entries based on the 'type' field (use processed entries)
        const expenseEntries = processedEntries?.filter(e => e.type === 'expense') || [];
        
        // Filter sale entries for income calculation (use processed entries)
        const saleEntries = processedEntries?.filter(e => e.type === 'sale') || [];
        
        
        
        
        // Debug: Log all sale entries with their amounts
        saleEntries.forEach((sale, index) => {
          
        });
        
        // Calculate expense totals grouped by currency.
        // Only count entries whose cost is currently recognized (excludes pending
        // checks, unsettled open accounts, and advance draw-downs) so the cost /
        // profit figures stay consistent with the project cost totals.
        const recognizedExpenseEntries = expenseEntries.filter(e => isExpenseCostRecognized(e));
        const expenseTotals = recognizedExpenseEntries.reduce((totals, entry) => {
          const currency = entry.original_currency || 'TRY';
          const amount = parseFloat(entry.original_amount || 0);
          
          if (!totals[currency]) {
            totals[currency] = 0;
          }
          totals[currency] += amount;
          
          return totals;
        }, {} as Record<string, number>);
        
        // Calculate income totals grouped by currency (from sales)
        const incomeTotals = saleEntries.reduce((totals, entry) => {
          const currency = entry.original_currency || 'TRY';
          const amount = parseFloat(entry.original_amount || 0);
          
          
          
          if (!totals[currency]) {
            totals[currency] = 0;
          }
          totals[currency] += amount;
          
          
          
          return totals;
        }, {} as Record<string, number>);
        
        // Calculate profit by currency
        const allCurrencies = new Set([...Object.keys(expenseTotals), ...Object.keys(incomeTotals)]);
        const profitTotals: Record<string, number> = {};
        
        allCurrencies.forEach(currency => {
          const income = incomeTotals[currency] || 0;
          const expense = expenseTotals[currency] || 0;
          profitTotals[currency] = income - expense;
        });
        
        
        
        
        
        
        // Create the HTML content for detailed report
        const projectName = project?.name || 'Proje Raporu';
        const companyName = userData?.company || userData?.name || 'ŞantiyeX Kullanıcısı';
        const userEmail = userData?.email || session.user.email || '';
        const reportDate = new Date().toLocaleDateString('tr-TR', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        
        // Create currency symbols mapping
        const currencySymbols: Record<string, string> = {
          'TRY': '₺',
          'USD': '$',
          'EUR': '€',
          'GBP': '£',
          'JPY': '¥',
          'RUB': '₽',
          'SAR': 'ر.س',
          'CHF': 'CHF',
          'XAU': 'oz',
          'BTC': '₿'
        };
        
        htmlContent = `
          <!DOCTYPE html>
          <html lang="tr">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${projectName} - Detaylı Rapor</title>
            <style>${commonStyles}</style>
          </head>
          <body>
            <div class="report-container">
              <!-- Report Header -->
              <div class="report-header">
                <h1>ŞantiyeX</h1>
                <div class="report-subtitle">Proje Yönetim Sistemi - Detaylı Rapor</div>
                <div class="company-info">
                  <div class="company-left">
                    <strong>Hazırlayan:</strong> ${companyName}<br>
                    <strong>E-posta:</strong> ${userEmail}<br>
                    <strong>Rapor Tarihi:</strong> ${reportDate}
                  </div>
                  <div class="company-right">
                    <strong>Rapor Türü:</strong> Detaylı Mali Rapor<br>
                    <strong>Dönem:</strong> ${reportData.includeAllRecords ? 'Tüm Kayıtlar' : `${reportData.startDate ? new Date(reportData.startDate).toLocaleDateString('tr-TR') : 'Başlangıç'} - ${reportData.endDate ? new Date(reportData.endDate).toLocaleDateString('tr-TR') : 'Günümüz'}`}<br>
                    <strong>Sayfa:</strong> 1
                  </div>
                </div>
              </div>

              <!-- Project Information -->
              <div class="project-info">
                <h2>📋 Proje Bilgileri</h2>
                <div class="project-details">
                  <div class="detail-item">
                    <div class="detail-label">Proje Adı</div>
                    <div class="detail-value">${projectName}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Proje Başlangıcı</div>
                    <div class="detail-value">${project?.start_date ? new Date(project.start_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Proje Konumu</div>
                    <div class="detail-value">${project?.location || 'Belirtilmemiş'}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Proje Açıklaması</div>
                    <div class="detail-value">${project?.description || 'Açıklama eklenmemiş'}</div>
                  </div>
                </div>
              </div>

              <!-- Financial Summary -->
              <div class="section-header">💰 Mali Durum Özeti</div>
              <table class="data-table">
                <tbody>
                  ${Array.from(allCurrencies).map(currency => {
                    const income = incomeTotals[currency] || 0;
                    const expense = expenseTotals[currency] || 0;
                    const profit = profitTotals[currency] || 0;
                    const symbol = currencySymbols[currency] || currency;
                    
                    return `
                      <tr>
                        <td><strong>Toplam Gelir (${currency})</strong></td>
                        <td class="amount">${symbol}${income.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                      </tr>
                      <tr>
                        <td><strong>Toplam Gider (${currency})</strong></td>
                        <td class="amount negative">${symbol}${expense.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                      </tr>
                      <tr style="background: ${profit >= 0 ? '#f0fdf4' : '#fef2f2'}; font-weight: bold;">
                        <td><strong>Net Kar/Zarar (${currency})</strong></td>
                        <td class="amount ${profit >= 0 ? '' : 'negative'}" style="font-weight: bold; color: ${profit >= 0 ? '#059669' : '#dc2626'};">
                          ${symbol}${profit.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                        </td>
                      </tr>
                      ${Array.from(allCurrencies).indexOf(currency) < allCurrencies.size - 1 ? '<tr><td colspan="2" style="border: none; padding: 5px;"></td></tr>' : ''}
                    `;
                  }).join('')}
                  ${allCurrencies.size === 0 ? `
                    <tr>
                      <td colspan="2" style="text-align: center; padding: 20px; color: #6b7280;">
                        Bu dönemde mali işlem kaydı bulunamadı<br>
                        <small style="font-size: 6px; margin-top: 5px; display: block;">
                          Rapor Dönemi: ${reportData.includeAllRecords ? 'Tüm Kayıtlar' : `${reportData.startDate ? new Date(reportData.startDate).toLocaleDateString('tr-TR') : 'Başlangıç'} - ${reportData.endDate ? new Date(reportData.endDate).toLocaleDateString('tr-TR') : 'Günümüz'}`}
                        </small>
                      </td>
                    </tr>
                  ` : `
                    <tr>
                      <td colspan="2" style="text-align: center; padding: 10px; font-size: 6px; color: #6b7280; border-top: 1px solid #e5e7eb;">
                        Rapor Dönemi: ${reportData.includeAllRecords ? 'Tüm Kayıtlar' : `${reportData.startDate ? new Date(reportData.startDate).toLocaleDateString('tr-TR') : 'Başlangıç'} - ${reportData.endDate ? new Date(reportData.endDate).toLocaleDateString('tr-TR') : 'Günümüz'}`}
                        <br>Toplam ${entries?.length || 0} kayıt analiz edildi (${expenseEntries.length} gider, ${saleEntries.length} satış)
                      </td>
                    </tr>
                  `}
                </tbody>
              </table>

              <!-- Detailed Entries -->
              <div class="section-header">📊 Tüm Kayıtlar</div>
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Açıklama</th>
                    <th>Kişi/Kategori</th>
                    <th>Tür</th>
                    <th>Para Birimi</th>
                    <th>Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  ${processedEntries && processedEntries.length > 0 ? processedEntries.map(entry => {
                    // Determine entry type and label
                    const entryType = entry.type || 'unknown';
                    let typeLabel = '';
                    let typeColor = '#6b7280';
                    
                    switch (entryType) {
                      case 'expense':
                        typeLabel = '📉 Expense';
                        typeColor = '#dc2626';
                        break;
                      case 'sale':
                        typeLabel = '💰 Sale';
                        typeColor = '#059669';
                        break;
                      case 'laborCost':
                        typeLabel = '👷 Labor';
                        typeColor = '#d97706';
                        break;
                      case 'givenCheck':
                        typeLabel = '📄 Given Check';
                        typeColor = '#7c3aed';
                        break;
                      case 'receivedCheck':
                        typeLabel = '📄 Received Check';
                        typeColor = '#059669';
                        break;
                      default:
                        typeLabel = '📈 Other';
                        typeColor = '#059669';
                    }
                    
                    // Use original_amount and original_currency for display
                    const displayAmount = parseFloat(entry.original_amount || 0);
                    const currency = entry.original_currency || 'TRY';
                    const currencySymbol = currencySymbols[currency] || currency;
                    
                    return `
                      <tr>
                        <td>${entry.date ? new Date(entry.date).toLocaleDateString('tr-TR') : 'Tarih yok'}</td>
                        <td>${entry.description || '-'}</td>
                        <td>${entry.person?.name || 'Genel'}</td>
                        <td><span style="color: ${typeColor}">${typeLabel}</span></td>
                        <td>${currency}</td>
                        <td class="amount">${currencySymbol}${displayAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</td>
                      </tr>
                    `;
                  }).join('') : '<tr><td colspan="6" style="text-align: center; padding: 20px; color: #6b7280;">Bu dönemde kayıt bulunamadı</td></tr>'}
                </tbody>
              </table>

              ${reportData.includePhotos && processedEntries && processedEntries.some(e => e.image_url) ? `
                <div class="section-header">📸 Proje Fotoğrafları</div>
                <div class="photo-grid">
                  ${processedEntries.filter(e => e.image_url).map(entry => `
                    <div class="photo-item">
                      <img src="${entry.image_url}" alt="${entry.description || 'Proje fotoğrafı'}" />
                      <div class="photo-caption">
                        <strong>${entry.description || 'Fotoğraf'}</strong><br>
                        ${entry.date ? new Date(entry.date).toLocaleDateString('tr-TR') : 'Tarih yok'}
                      </div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}

              <!-- Report Footer -->
              <div class="report-footer">
                <div>Bu rapor ŞantiyeX Proje Yönetim Sistemi tarafından otomatik olarak oluşturulmuştur.</div>
                <div>Rapor Oluşturma Zamanı: ${new Date().toLocaleString('tr-TR')}</div>
              </div>
            </div>
          </body>
          </html>
        `;
      } else {
        // For general report (genel rapor), get only progress stages, completion dates, notes and photos
        const { data: stages, error: stagesError } = await supabase
          .from('construction_stages')
          .select('*')
          .eq('project_id', reportData.projectId)
          .order('order_index', { ascending: true });

        if (stagesError) {
          
          return { data: null, error: stagesError };
        }

        // Process stage images - convert storage references to signed URLs
        const processedStages = await processStageImagesForReport(stages || []);
        
        // Organize stages by phase (use processed stages)
        const phaseMap = new Map();
        if (processedStages) {
          processedStages.forEach(stage => {
            if (!phaseMap.has(stage.phase)) {
              phaseMap.set(stage.phase, []);
            }
            phaseMap.get(stage.phase).push(stage);
          });
        }
        
        // Create the HTML content for general report
        const projectName = project?.name || 'Proje Raporu';
        const companyName = userData?.company || userData?.name || 'ŞantiyeX Kullanıcısı';
        const userEmail = userData?.email || session.user.email || '';
        const reportDate = new Date().toLocaleDateString('tr-TR', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });

        // Calculate completion statistics (use processed stages)
        const totalStages = processedStages?.length || 0;
        const completedStages = processedStages?.filter(s => s.completed).length || 0;
        const completionPercentage = totalStages > 0 ? Math.round((completedStages / totalStages) * 100) : 0;
        
        htmlContent = `
          <!DOCTYPE html>
          <html lang="tr">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${projectName} - Genel Rapor</title>
            <style>${commonStyles}</style>
          </head>
          <body>
            <div class="report-container">
              <!-- Report Header -->
              <div class="report-header">
                <h1>ŞantiyeX</h1>
                <div class="report-subtitle">Proje Yönetim Sistemi - İlerleme Raporu</div>
                <div class="company-info">
                  <div class="company-left">
                    <strong>Hazırlayan:</strong> ${companyName}<br>
                    <strong>E-posta:</strong> ${userEmail}<br>
                    <strong>Rapor Tarihi:</strong> ${reportDate}
                  </div>
                  <div class="company-right">
                    <strong>Rapor Türü:</strong> Genel İlerleme Raporu<br>
                    <strong>Tamamlanma:</strong> %${completionPercentage}<br>
                    <strong>Sayfa:</strong> 1
                  </div>
                </div>
              </div>

              <!-- Project Information -->
              <div class="project-info">
                <h2>📋 Proje Bilgileri</h2>
                <div class="project-details">
                  <div class="detail-item">
                    <div class="detail-label">Proje Adı</div>
                    <div class="detail-value">${projectName}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Proje Başlangıcı</div>
                    <div class="detail-value">${project?.start_date ? new Date(project.start_date).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Proje Konumu</div>
                    <div class="detail-value">${project?.location || 'Belirtilmemiş'}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">İlerleme Durumu</div>
                    <div class="detail-value">${completedStages}/${totalStages} aşama tamamlandı (%${completionPercentage})</div>
                  </div>
                </div>
              </div>

              <!-- Progress Summary -->
              <div class="section-header">📈 İlerleme Özeti</div>
              <table class="data-table">
                <tbody>
                  <tr>
                    <td><strong>Toplam Aşama Sayısı</strong></td>
                    <td>${totalStages}</td>
                  </tr>
                  <tr>
                    <td><strong>Tamamlanan Aşamalar</strong></td>
                    <td style="color: #059669; font-weight: bold;">${completedStages}</td>
                  </tr>
                  <tr>
                    <td><strong>Devam Eden Aşamalar</strong></td>
                    <td style="color: #d97706; font-weight: bold;">${totalStages - completedStages}</td>
                  </tr>
                  <tr style="background: #f3f4f6; font-weight: bold;">
                    <td><strong>Genel Tamamlanma Oranı</strong></td>
                    <td style="color: #4F46E5;">%${completionPercentage}</td>
                  </tr>
                </tbody>
              </table>

              <!-- Project Phases -->
              ${Array.from(phaseMap.entries()).map(([phase, phaseStages]) => `
                <div class="phase-header">${phase}</div>
                <ul class="stage-list">
                  ${phaseStages.map(stage => `
                    <li class="stage-item ${stage.completed ? 'completed' : ''}">
                      <div class="stage-header">
                        <div>
                          <h3 class="stage-title">${stage.name}</h3>
                          ${stage.completed && stage.completed_date ? `
                            <div class="stage-date">✅ Tamamlandı: ${new Date(stage.completed_date).toLocaleDateString('tr-TR')}</div>
                          ` : `
                            <div class="stage-date">⏳ Devam ediyor</div>
                          `}
                        </div>
                        <span class="completion-badge ${stage.completed ? 'done' : 'pending'}">
                          ${stage.completed ? '✓' : '⌛'}
                        </span>
                      </div>
                      ${stage.note ? `<div class="stage-note">📝 ${stage.note}</div>` : ''}
                      ${reportData.includePhotos && stage.photo_url ? `
                        <div class="stage-photo">
                          <img src="${stage.photo_url}" alt="${stage.name} fotoğrafı" />
                        </div>
                      ` : ''}
                    </li>
                  `).join('')}
                </ul>
              `).join('')}

              <!-- Report Footer -->
              <div class="report-footer">
                <div>Bu rapor ŞantiyeX Proje Yönetim Sistemi tarafından otomatik olarak oluşturulmuştur.</div>
                <div>Rapor Oluşturma Zamanı: ${new Date().toLocaleString('tr-TR')}</div>
              </div>
            </div>
          </body>
          </html>
        `;
      }

      
      
      return { data: { htmlContent }, error: null };
    } catch (error) {
      
      return { data: null, error };
    }
  }
}